<template>
    <container>
        <hox-loading-layer v-if="isLoading && !isInitialLoad" size="large" />

        <AddCampaignSectionModal
            v-if="addSectionModalIsVisible"
            :client-id="clientId"
            @close="toggleAddSectionModalIsVisible"
            @sectionAdded="reloadCampaignsAndSections"
        />

        <CreateCampaignModal
            v-if="createCampaignModalIsVisible"
            :sections="sections"
            :has-creative-x-enabled="hasCreativeXEnabled"
            :client-creative-x-secret-id="clientCreativeXSecretId"
            :has-satalia-enabled="hasSataliaEnabled"
            @campaignCreated="reloadCampaignsAndSections"
            @close="toggleCreateCampaignModalIsVisible"
        />

        <DuplicateCampaignModal
            v-if="duplicateCampaignModalIsVisible && campaignIdToDuplicate"
            :campaign-id="campaignIdToDuplicate"
            :sections="sections"
            :has-creative-x-enabled="hasCreativeXEnabled"
            :has-connect-enabled="hasConnectEnabled"
            :client-creative-x-secret-id="clientCreativeXSecretId"
            :has-satalia-enabled="hasSataliaEnabled"
            @campaignDuplicated="reloadCampaignsAndSections"
            @close="toggleDuplicateCampaignModalIsVisible"
        />

        <EditCampaignModal
            v-if="editCampaignModalIsVisible && campaignIdToEdit"
            :campaign-id="campaignIdToEdit"
            :sections="sections"
            :has-creative-x-enabled="hasCreativeXEnabled"
            :client-creative-x-secret-id="clientCreativeXSecretId"
            :has-satalia-enabled="hasSataliaEnabled"
            @campaignEdited="reloadCampaignsAndSections"
            @close="toggleEditCampaignModalIsVisible"
            @deleted="reloadCampaignsAndSections"
        />

        <EditCampaignSectionModal
            v-if="editCampaignSectionModalIsVisible && sectionToEdit"
            :section-id="sectionToEdit._id"
            :section-name="sectionToEdit.name"
            @close="toggleEditCampaignSectionModalIsVisible"
            @deleted="reloadCampaignsAndSections"
            @sectionUpdated="reloadCampaignsAndSections"
        />

        <SetCampaignSectionModal
            v-if="setCampaignSectionModalIsVisible && campaignIdToEdit"
            :campaign-id="campaignIdToEdit"
            :sections="sections"
            @close="toggleSetCampaignSectionModalIsVisible"
            @campaignSectionSet="reloadCampaignsAndSections"
        />

        <TeamModal
            v-if="teamModalIsVisible"
            :campaign-id="teamCampaignId"
            :client-id="clientId"
            entity-type="campaign"
            @close="toggleTeamModalIsVisible"
        />

        <div v-if="isLoading && isInitialLoad" class="campaigns-dashboard__loading-wrapper">
            <hox-loading-layer size="large" />
        </div>

        <div v-else-if="hasCampaigns" class="campaigns">
            <div class="campaigns__action-items">
                <SortCampaigns v-model="sortCampaignsBy" class="campaigns__action-item" />

                <Button
                    v-if="userCanManageCampaignSections"
                    class="campaigns__action-item"
                    size="large"
                    data-testid="add-section__button"
                    @click="toggleAddSectionModalIsVisible"
                >
                    Add new section
                </Button>

                <Button
                    v-if="userCanAddCampaign"
                    class="campaigns__action-item"
                    size="large"
                    type="primary"
                    data-testid="create-campaign__button"
                    @click="toggleCreateCampaignModalIsVisible"
                >
                    Create new campaign
                </Button>
            </div>

            <CampaignsList
                v-if="hasCampaigns"
                :campaigns="campaignsBySectionId.noSection"
                @duplicate="showDuplicateCampaignModal"
                @edit="showEditCampaignModal"
                @manageTeam="showTeam"
                @setSection="showSetCampaignSectionModal"
            />

            <CampaignsListDraggable
                v-model:ordered-sections="orderedSections"
                :sections-by-id="sectionsById"
                :campaigns-by-section-id="campaignsBySectionId"
                :client-id="clientId"
                :user-can-manage-campaign-sections="userCanManageCampaignSections"
                @editSection="showEditCampaignSectionModal"
                @duplicateCampaign="showDuplicateCampaignModal"
                @editCampaign="showEditCampaignModal"
                @manageTeam="showTeam"
                @setSection="showSetCampaignSectionModal"
            />
        </div>

        <NoCampaignsMessage
            v-else
            :user-can-add-campaign="userCanAddCampaign"
            @toggleCreateCampaignModalIsVisible="toggleCreateCampaignModalIsVisible"
        />
    </container>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import campaignsQuery from "@/apollo/queries/CampaignsByClient.gql";
import campaignSectionsQuery from "@/apollo/queries/CampaignSections.gql";
import AddCampaignSectionModal from "@/components/AddCampaignSectionModal.vue";
import CampaignsList from "@/components/CampaignsListing/CampaignsList.vue";
import CampaignsListDraggable from "@/components/CampaignsListing/CampaignsListDraggable.vue";
import NoCampaignsMessage from "@/components/CampaignsListing/NoCampaignsMessage.vue";
import Container from "@/components/Container.vue";
import CreateCampaignModal from "@/components/CreateCampaignModal.vue";
import DuplicateCampaignModal from "@/components/DuplicateCampaignModal.vue";
import EditCampaignModal from "@/components/EditCampaignModal.vue";
import EditCampaignSectionModal from "@/components/EditCampaignSectionModal.vue";
import SetCampaignSectionModal from "@/components/SetCampaignSectionModal.vue";
import SortCampaigns from "@/components/SortCampaign.vue";
import TeamModal from "@/components/TeamModal.vue";
import { type Campaign, type CampaignSection } from "@/types/types";
import { defaultCampaignSortBy } from "@/enums/sort";
import { AppRouteNames } from "@/router/constants";

const LocalStorageOrderItem = "hoxSectionsOrder";

export type SectionsWithCampaigns = {
    noSection: Campaign[];
    [key: string]: Campaign[];
};

export default defineComponent({
    name: "CampaignsDashboard",
    components: {
        AddCampaignSectionModal,
        SortCampaigns,
        Container,
        CreateCampaignModal,
        DuplicateCampaignModal,
        EditCampaignModal,
        EditCampaignSectionModal,
        SetCampaignSectionModal,
        CampaignsListDraggable,
        CampaignsList,
        NoCampaignsMessage,
        TeamModal
    },

    data() {
        return {
            campaigns: [] as Campaign[],
            sections: [] as CampaignSection[],

            addSectionModalIsVisible: false,
            editCampaignModalIsVisible: false,
            createCampaignModalIsVisible: false,
            duplicateCampaignModalIsVisible: false,
            editCampaignSectionModalIsVisible: false,
            setCampaignSectionModalIsVisible: false,
            teamModalIsVisible: false,

            campaignIdToDuplicate: undefined as string | undefined,
            campaignIdToEdit: undefined as string | undefined,
            sectionIdToEdit: undefined as string | undefined,
            teamCampaignId: undefined as string | undefined,
            oldClientId: null,

            hasLoadedCampaigns: false,
            hasLoadedSections: false,
            isLoadingCampaigns: 0,
            isLoadingSections: 0,

            localSortOrder: [] as string[],
            sortCampaignsBy: defaultCampaignSortBy
        };
    },

    computed: {
        campaignsBySectionId() {
            const campaignsBySectionId: SectionsWithCampaigns = {
                noSection: []
            };

            this.campaigns.forEach(campaign => {
                const sectionId = campaign.section?._id || "noSection";

                if (!campaignsBySectionId[sectionId]) {
                    campaignsBySectionId[sectionId] = [];
                }

                campaignsBySectionId[sectionId].push(campaign);
            });

            return campaignsBySectionId;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        hasCampaigns() {
            return this.campaigns?.length > 0;
        },

        hasCreativeXEnabled() {
            return this.$store.state.ui.currentClient?.creativeXPlatformSettings?.enabled ?? false;
        },

        hasConnectEnabled() {
            return this.$store.state.ui.currentClient?.connectModuleEnabled ?? false;
        },

        clientCreativeXSecretId() {
            return this.$store.state.ui.currentClient?.creativeXPlatformSettings?.enabled
                ? (this.$store.state.ui.currentClient?.creativeXPlatformSettings?.secretId ?? "")
                : "";
        },

        hasSataliaEnabled() {
            return this.$store.state.ui.currentClient?.sataliaPlatformSettings?.enabled ?? false;
        },

        isInitialLoad() {
            return !this.hasLoadedCampaigns || !this.hasLoadedSections;
        },

        isLoading() {
            return this.isLoadingCampaigns || this.isLoadingSections;
        },

        isPermissionGranted() {
            return this.userHasScope;
        },

        sectionToEdit() {
            return this.sections.find(section => {
                return section._id === this.sectionIdToEdit;
            });
        },

        sectionsById() {
            return this.visibleSections.reduce<Record<string, { name: string }>>((acc, section) => {
                acc[section._id] = section;

                return acc;
            }, {});
        },

        userCanAddCampaign() {
            return this.$auth.userCan(this.$auth.Actions.CanAddCampaign, { clientId: this.clientId }, false);
        },

        userCanManageCampaignSections() {
            return this.$auth.userCan(this.$auth.Actions.CanManageCampaignSections, { clientId: this.clientId }, false);
        },

        userHasScope() {
            return this.$auth.hasScope({ clientId: this.clientId }, false);
        },

        userScope() {
            return this.$store.state.auth?.me?.scope;
        },

        visibleSections() {
            if (this.userCanManageCampaignSections) {
                return this.sections;
            }

            return this.sections.filter(section => {
                return this.campaignsBySectionId[section._id] !== undefined;
            });
        },

        orderedSections: {
            get() {
                const dbSectionIds = this.visibleSections.map(({ _id }) => _id);
                const localStorageTagByIndex = this.localSortOrder.reduce<Record<string, number>>(
                    (acc, sectionId, index) => {
                        acc[sectionId] = index + 1;
                        return acc;
                    },
                    {}
                );

                const { newTags, existingTags } = dbSectionIds.reduce<{ newTags: string[]; existingTags: string[] }>(
                    (acc, sectionId) => {
                        if (this.localSortOrder.includes(sectionId)) {
                            acc.existingTags.push(sectionId);
                        } else {
                            acc.newTags.push(sectionId);
                        }

                        return acc;
                    },
                    { newTags: [], existingTags: [] }
                );

                const res = [
                    ...existingTags.sort((tagA, tagB) => localStorageTagByIndex[tagA] - localStorageTagByIndex[tagB]),
                    ...newTags
                ];

                return res;
            },
            set(val: string[]) {
                this.localSortOrder = val;
            }
        },

        localStorageSectionOrder() {
            const orders: Record<string, string[]> = JSON.parse(localStorage.getItem(LocalStorageOrderItem) || "{}");

            if (!orders?.[this.clientId]) {
                return [];
            }

            return orders[this.clientId];
        }
    },

    watch: {
        localSortOrder(clientOrder: string[]) {
            const orders: Record<string, string[]> = JSON.parse(localStorage.getItem(LocalStorageOrderItem) || "{}");
            orders[this.clientId] = clientOrder;
            localStorage.setItem(LocalStorageOrderItem, JSON.stringify(orders));
        },

        userHasScope: {
            immediate: true,
            handler() {
                if (this.userHasScope) {
                    return;
                }

                this.$snackbar.warning("You don't have permission to access this client");
                this.$router.push({ name: AppRouteNames.HOME }).catch(err => {
                    if (err.name !== "NavigationDuplicated") {
                        throw err;
                    }
                });
            }
        },

        userScope: {
            handler() {
                if (
                    this.campaigns.some(
                        campaign =>
                            !this.$auth.hasScope({
                                clientId: this.clientId,
                                campaignId: campaign._id
                            })
                    )
                ) {
                    this.$apollo.queries.campaigns.refetch();
                }
            }
        }
    },

    created() {
        this.localSortOrder = this.localStorageSectionOrder;
    },

    methods: {
        reloadCampaignsAndSections() {
            this.$apollo.queries.campaigns.refetch();
            this.$apollo.queries.campaignSections.refetch();
        },
        showDuplicateCampaignModal(campaignId: string) {
            this.campaignIdToDuplicate = campaignId;
            this.toggleDuplicateCampaignModalIsVisible();
        },
        showEditCampaignModal(campaignId: string) {
            this.campaignIdToEdit = campaignId;
            this.toggleEditCampaignModalIsVisible();
        },
        showEditCampaignSectionModal(sectionId: string) {
            this.sectionIdToEdit = sectionId;
            this.toggleEditCampaignSectionModalIsVisible();
        },
        showSetCampaignSectionModal(campaignId: string) {
            this.campaignIdToEdit = campaignId;
            this.toggleSetCampaignSectionModalIsVisible();
        },
        showTeam(campaignId: string) {
            this.teamCampaignId = campaignId;
            this.toggleTeamModalIsVisible();
        },
        toggleAddSectionModalIsVisible() {
            this.addSectionModalIsVisible = !this.addSectionModalIsVisible;
        },
        toggleCreateCampaignModalIsVisible() {
            this.createCampaignModalIsVisible = !this.createCampaignModalIsVisible;
        },
        toggleDuplicateCampaignModalIsVisible() {
            this.duplicateCampaignModalIsVisible = !this.duplicateCampaignModalIsVisible;
        },
        toggleEditCampaignModalIsVisible() {
            this.editCampaignModalIsVisible = !this.editCampaignModalIsVisible;
        },
        toggleEditCampaignSectionModalIsVisible() {
            this.editCampaignSectionModalIsVisible = !this.editCampaignSectionModalIsVisible;
        },
        toggleSetCampaignSectionModalIsVisible() {
            this.setCampaignSectionModalIsVisible = !this.setCampaignSectionModalIsVisible;
        },
        toggleTeamModalIsVisible() {
            this.teamModalIsVisible = !this.teamModalIsVisible;
        }
    },

    apollo: {
        campaigns: {
            query: campaignsQuery,
            fetchPolicy: "network-only",
            loadingKey: "isLoadingCampaigns",
            variables() {
                return {
                    clientId: this.clientId,
                    orderBy: this.sortCampaignsBy,
                    wppProjectId: this.$store.state.auth?.wppOsContext?.project?.id
                };
            },

            skip() {
                return !this.isPermissionGranted;
            },

            result({ data, error }) {
                if (error) {
                    this.$snackbar.error("Failed to load campaigns.", error.message);
                    return;
                }

                this.hasLoadedCampaigns = true;

                if (!data.client) {
                    this.$snackbar.warning("The client you're looking for doesn't exist or may have been deleted");

                    this.$router.push({ name: AppRouteNames.HOME }).catch(err => {
                        if (err.name !== "NavigationDuplicated") {
                            throw err;
                        }
                    });
                } else {
                    // TEMPORARY UNTIL OLD IDS ARE REMOVED AND DB DATA MIGRATED
                    this.oldClientId = data.client?.id;
                }
            }
        },

        campaignSections: {
            query: campaignSectionsQuery,
            fetchPolicy: "network-only",
            loadingKey: "isLoadingSections",

            variables() {
                return {
                    clientId: this.clientId,
                    orderBy: this.sortCampaignsBy
                };
            },

            skip() {
                return !this.isPermissionGranted;
            },

            result({ data, error }) {
                if (error) {
                    this.$snackbar.error("Failed to load sections.", error.message);
                    return;
                }
                this.hasLoadedSections = true;
                this.sections = data.campaignSections;
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.campaigns__action-items {
    display: flex;
    justify-content: flex-end;
    padding: 15px 0;
}

.campaigns__list--no-action-items {
    padding: 15px 0 0 0;
}

.campaigns__action-item {
    flex: 0 0;
    display: flex;
    align-items: center;
    margin-left: 20px;
}

.campaigns-dashboard__loading-wrapper {
    height: 120px;
    position: relative;
}
</style>
